import SectionPanel from '@/components/SectionPanel';
import { Box, Grid, GridItem, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { formatMinutes, deLocaleOption } from "@/data/DateTimeUtil";
import WaitingSpinner from "@/components/WaitingSpinner";
import { useProjectNames } from "@/hooks/useProjectNames";
import YearNavigation from "./YearNavigation";
import { MonthlySummary } from "@/api/MonthlySummary";
import { workmodelService } from "@/service/WorkModelService";
import { timesheetDataMap } from "../TimesheetData";
import { useAuthContext } from "@/auth/AuthContext";
import { usePageRefresh } from "@/hooks/usePageRefresh";
import { Alert } from "@/components/ui/alert";
import { useI18n } from "@/i18n/I18nContext";

type YearlyReportProps = {
  userId: number;
  organizationId: number;
  active?: boolean;
};

const YearlyReport = ({ userId, organizationId, active = true }: YearlyReportProps) => {
  const { user } = useAuthContext();
  const token = user?.token;
  const revision = usePageRefresh();
  const { t } = useI18n();
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const { nameById: projects, isLoading: isProjectNamesLoading } = useProjectNames();
  const [data, setData] = useState<Map<number, MonthlySummary>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const monthNames = Array.from({ length: 12 }, (_, i) =>
    format(new Date(currentYear, i, 1), 'MMMM', deLocaleOption)
  );

  useEffect(() => {
    let current = true;
    setData(new Map());
    setError(false);
    setIsLoading(false);
    if (!active || !token || !userId || organizationId !== user?.data.organizationId) return;
    setIsLoading(true);
    workmodelService.yearlyReport(userId, currentYear)
      .then(rows => { if (current) setData(new Map(rows.map(row => [row.contract.month, row]))); })
      .catch(() => { if (current) setError(true); })
      .finally(() => { if (current) setIsLoading(false); });
    return () => { current = false; };
  }, [currentYear, userId, organizationId, user?.data.organizationId, token, revision, active]);

  // extract all project and task ids from the year
  const projectIds = new Set<number>();
  const taskIds = new Set<number>();
  Array.from(data.values()).forEach(summary => {
    Object.keys(summary.projects).forEach(k => projectIds.add(Number(k)));
    Object.keys(summary.tasks).forEach(k => taskIds.add(Number(k)));
  });

  const getProjectData = () => {
    const projectData = new Map<string, Map<number, number>>();

    projectIds.forEach(projectId => {
      const projectName = projects.get(projectId) || 'Unbekanntes Projekt';
      const monthData = new Map<number, number>();

      for (let m = 0; m < 12; m++) {
        const monthlySummary = data.get(m);
        if (monthlySummary) {
          const mapper = new Map<number, number>(Object.entries(monthlySummary.projects).map(([k, v]) => [Number(k), Number(v)]));
          const minutes = mapper.get(projectId) || 0;
          monthData.set(m, minutes);
        } else {
          monthData.set(m, 0);
        }
      }

      projectData.set(projectName, monthData);
    });

    return projectData;
  };

  const projectData = getProjectData();
  const projectNames = Array.from(projectData.keys())
    .filter(projectName => {
      const monthData = projectData.get(projectName)!;
      return Array.from(monthData.values()).reduce((sum, minutes) => sum + minutes, 0) > 0;
    })
    .sort();

  const getTaskData = () => {
    const taskData = new Map<string, Map<number, number>>();
    taskIds.forEach(taskId => {
      const taskInfo = timesheetDataMap.get(taskId);
      const taskName = taskInfo ? (taskInfo.detail ? `${taskInfo.name} - ${taskInfo.detail}` : taskInfo.name) : `Task #${taskId}`;
      const monthData = new Map<number, number>();
      for (let m = 0; m < 12; m++) {
        const monthlySummary = data.get(m);
        if (monthlySummary) {
          const mapper = new Map<number, number>(Object.entries(monthlySummary.tasks).map(([k, v]) => [Number(k), Number(v)]));
          const minutes = mapper.get(taskId) || 0;
          monthData.set(m, minutes);
        } else {
          monthData.set(m, 0);
        }
      }
      taskData.set(taskName, monthData);
    });
    return taskData;
  };

  const taskData = getTaskData();
  const taskNames = Array.from(taskData.keys())
    .filter(taskName => {
      const monthData = taskData.get(taskName)!;
      return Array.from(monthData.values()).reduce((sum, minutes) => sum + minutes, 0) > 0;
    })
    .sort();

  const getEntriesByMonth = () => {
    const entries = new Map<number, number>();
    for (let m = 0; m < 12; m++) {
      const monthlySummary = data.get(m);
      entries.set(m, monthlySummary ? monthlySummary.entries : 0);
    }
    return entries;
  };

  const entriesByMonth = getEntriesByMonth();

  return (
    <SectionPanel>
      <YearNavigation currentYear={currentYear} setCurrentYear={setCurrentYear} />
      {(isLoading || isProjectNamesLoading) && <WaitingSpinner size='lg' mb='1rem' />}
      {error && <Alert role="alert" status="error" title={t("publicHolidays.summaryError")} />}

      <Box overflowX='auto' minW='0'>
        <Grid templateColumns={`200px repeat(12, 1fr)`} gap={'2px'} minW='800px'>
          <GridItem p={2} fontWeight='bold' bg='blue.subtle' borderWidth='1px' borderColor='border' borderRadius='md' display='flex' alignItems='center'>
            Kategorie
          </GridItem>
          {monthNames.map((month, index) => (
            <GridItem
              key={index}
              p={2}
              fontWeight='bold'
              bg='blue.muted'
              borderWidth='1px' borderColor='border'
              borderRadius='md'
              textAlign='center'
              display='flex'
              alignItems='center'
              justifyContent='center'
            >
              {month}
            </GridItem>
          ))}
          {/* Projects section */}
          <GridItem colSpan={13} p={2} fontWeight='bold' bg='bg.emphasized' borderWidth='1px' borderColor='border' borderRadius='md' display='flex' alignItems='center'>
            Projekte
          </GridItem>

          {projectNames.map((projectName, rowIndex) => {
            const monthData = projectData.get(projectName)!;
            const totalMinutes = Array.from(monthData.values()).reduce((sum, minutes) => sum + minutes, 0);

            return [
              <GridItem
                key={`${rowIndex}-name`}
                p={2}
                bg={rowIndex % 2 === 0 ? 'bg.subtle' : 'bg.panel'}
                borderWidth='1px' borderColor='border'
                borderRadius='md'
                display='flex'
                flexDirection='column'
                justifyContent='center'
              >
                <Text fontSize='sm' fontWeight='medium'>{projectName}</Text>
                <Text fontSize='xs' color='fg.muted'>{formatMinutes(totalMinutes)}</Text>
              </GridItem>,
              ...monthNames.map((_, monthIndex) => {
                const minutes = monthData.get(monthIndex) || 0;
                return (
                  <GridItem
                    key={`${rowIndex}-${monthIndex}`}
                    p={2}
                    bg={rowIndex % 2 === 0 ? 'bg.subtle' : 'bg.panel'}
                    borderWidth='1px' borderColor='border'
                    borderRadius='md'
                    textAlign='center'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    {minutes > 0 ? (
                      <Text fontSize='sm'>{formatMinutes(minutes)}</Text>
                    ) : (
                      <Text fontSize='sm' color='fg.subtle'>-</Text>
                    )}
                  </GridItem>
                );
              })
            ];
          })}

          {/* Tasks section */}
          <GridItem colSpan={13} p={2} fontWeight='bold' bg='bg.emphasized' borderWidth='1px' borderColor='border' borderRadius='md' mt={2} display='flex' alignItems='center'>
            Tasks
          </GridItem>
          {taskNames.map((taskName, rowIndex) => {
            const monthData = taskData.get(taskName)!;
            const totalMinutes = Array.from(monthData.values()).reduce((sum, minutes) => sum + minutes, 0);
            return [
              <GridItem
                key={`task-${rowIndex}-name`}
                p={2}
                bg={rowIndex % 2 === 0 ? 'bg.subtle' : 'bg.panel'}
                borderWidth='1px' borderColor='border'
                borderRadius='md'
                display='flex'
                flexDirection='column'
                justifyContent='center'
              >
                <Text fontSize='sm' fontWeight='medium'>{taskName}</Text>
                <Text fontSize='xs' color='fg.muted'>{formatMinutes(totalMinutes)}</Text>
              </GridItem>,
              ...monthNames.map((_, monthIndex) => {
                const minutes = monthData.get(monthIndex) || 0;
                return (
                  <GridItem
                    key={`task-${rowIndex}-${monthIndex}`}
                    p={2}
                    bg={rowIndex % 2 === 0 ? 'bg.subtle' : 'bg.panel'}
                    borderWidth='1px' borderColor='border'
                    borderRadius='md'
                    textAlign='center'
                    display='flex'
                    alignItems='center'
                    justifyContent='center'
                  >
                    {minutes > 0 ? (
                      <Text fontSize='sm'>{formatMinutes(minutes)}</Text>
                    ) : (
                      <Text fontSize='sm' color='fg.subtle'>-</Text>
                    )}
                  </GridItem>
                );
              })
            ];
          })}

          {/* Entries section */}
          <GridItem colSpan={13} p={2} fontWeight='bold' bg='bg.emphasized' borderWidth='1px' borderColor='border' borderRadius='md' mt={2} display='flex' alignItems='center'>
            Allgemein
          </GridItem>
          <GridItem p={2} bg='bg.subtle' borderWidth='1px' borderColor='border' borderRadius='md' display='flex' flexDirection='column' justifyContent='center'>
            <Text fontSize='sm' fontWeight='medium'>Allgemein</Text>
            <Text fontSize='xs' color='fg.muted'>
              {formatMinutes(Array.from(entriesByMonth.values()).reduce((sum, v) => sum + v, 0))}
            </Text>
          </GridItem>
          {monthNames.map((_, monthIndex) => {
            const minutes = entriesByMonth.get(monthIndex) || 0;
            return (
              <GridItem
                key={`entries-${monthIndex}`}
                p={2}
                bg='bg.subtle'
                borderWidth='1px' borderColor='border'
                borderRadius='md'
                textAlign='center'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                {minutes > 0 ? (
                  <Text fontSize='sm'>{formatMinutes(minutes)}</Text>
                ) : (
                  <Text fontSize='sm' color='fg.subtle'>-</Text>
                )}
              </GridItem>
            );
          })}

          <GridItem p={2} fontWeight='bold' bg='blue.subtle' borderWidth='1px' borderColor='blue.500' borderRadius='md' display='flex' flexDirection='column' justifyContent='center'>
            Total
            <Text fontSize='sm'>{formatMinutes(
                Array.from(projectData.values()).reduce((sum, monthData) =>
                  sum + Array.from(monthData.values()).reduce((s, v) => s + v, 0), 0) +
                Array.from(taskData.values()).reduce((sum, monthData) =>
                  sum + Array.from(monthData.values()).reduce((s, v) => s + v, 0), 0) +
                Array.from(entriesByMonth.values()).reduce((sum, v) => sum + v, 0)
            )}</Text>
          </GridItem>
          {monthNames.map((_, monthIndex) => {
            const projectTotal = Array.from(projectData.values())
              .reduce((sum, monthData) => sum + (monthData.get(monthIndex) || 0), 0);
            const taskTotal = Array.from(taskData.values())
              .reduce((sum, monthData) => sum + (monthData.get(monthIndex) || 0), 0);
            const entriesTotal = entriesByMonth.get(monthIndex) || 0;
            const monthTotal = projectTotal + taskTotal + entriesTotal;
            return (
              <GridItem
                key={`total-${monthIndex}`}
                p={2}
                fontWeight='bold'
                bg='blue.muted'
                borderWidth='1px' borderColor='blue.500'
                borderRadius='md'
                textAlign='center'
                display='flex'
                alignItems='center'
                justifyContent='center'
              >
                {monthTotal > 0 ? formatMinutes(monthTotal) : '-'}
              </GridItem>
            );
          })}
        </Grid>
      </Box>
    </SectionPanel>
  );
};

export default YearlyReport;
